import React, { useState, useEffect } from "react";
import MainCalculatorsList from "../FoodCalculator/MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "حاسبة الايس كريم - البوظة | حساب مقادير ومكونات المثلجات بدقة";
const DESCRIPTION =
  "حاسبة الايس كريم المجانية - احسب مقادير ومكونات الايس كريم المنزلي والبوظة بدقة. أداة متطورة لحساب نسب السكر والدهون والمواد الصلبة للحصول على قوام مثالي.";
const KEYWORDS =
  "حاسبة الايس كريم, حساب مكونات البوظة, مقادير الايس كريم, حاسبة المثلجات, صناعة الايس كريم المنزلي, ice cream calculator, gelato calculator";
const OG_URL = "https://do-calculate.com/calculator/ar/ice-cream/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "حاسبة الايس كريم - البوظة",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "الحاسبات",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "حاسبة الايس كريم",
        item: OG_URL,
      },
    ],
  },
];

function IceCreamCalculator() {
  // States for Inputs
  const [totalWeight, setTotalWeight] = useState(1000);
  const [fatPercent, setFatPercent] = useState(10);
  const [msnfPercent, setMsnfPercent] = useState(10.5); // Milk Solids Not Fat
  const [sugarPercent, setSugarPercent] = useState(15);
  const [stabilizerPercent, setStabilizerPercent] = useState(0.5);

  // States for Results
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateIceCream = () => {
    try {
      setError("");

      const weight = parseFloat(totalWeight);
      const fatP = parseFloat(fatPercent);
      const msnfP = parseFloat(msnfPercent);
      const sugarP = parseFloat(sugarPercent);
      const stabP = parseFloat(stabilizerPercent);

      if (weight <= 0 || fatP < 0 || msnfP < 0 || sugarP < 0 || stabP < 0) {
        setError("يرجى إدخال قيم صحيحة أكبر من الصفر");
        setResult(null);
        return;
      }

      if (fatP + msnfP + sugarP + stabP >= 100) {
        setError("مجموع نسب المكونات الصلبة يجب أن يكون أقل من 100%");
        setResult(null);
        return;
      }

      // Calculations based on standard Pearson Square logic for ice cream balancing
      const fatWeight = (weight * fatP) / 100;
      const msnfWeight = (weight * msnfP) / 100;
      const sugarWeight = (weight * sugarP) / 100;
      const stabilizerWeight = (weight * stabP) / 100;
      const waterWeight = weight - (fatWeight + msnfWeight + sugarWeight + stabilizerWeight);

      // Total Solids
      const totalSolids = fatP + msnfP + sugarP + stabP;

      setResult({
        fatWeight,
        msnfWeight,
        sugarWeight,
        stabilizerWeight,
        waterWeight,
        totalSolids,
        batchSize: weight
      });
    } catch (err) {
      setError("حدث خطأ في الحساب. تأكد من إدخال أرقام صحيحة.");
      setResult(null);
    }
  };

  useEffect(() => {
    calculateIceCream();
  }, [totalWeight, fatPercent, msnfPercent, sugarPercent, stabilizerPercent]);

  const handleClear = () => {
    setTotalWeight(1000);
    setFatPercent(10);
    setMsnfPercent(10.5);
    setSugarPercent(15);
    setStabilizerPercent(0.5);
    setResult(null);
    setError("");
  };

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>حاسبة الايس كريم - البوظة</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                تساعدك <strong>حاسبة الايس كريم</strong> على موازنة مكونات الخليط (Mix) للحصول على قوام احترافي. 
                تعتمد جودة البوظة على التوازن الدقيق بين الدهون، السكريات، والمواد الصلبة اللادهنية (MSNF) لمنع تكون بلورات الثلج وضمان نعومة القوام.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>مدخلات خليط الايس كريم</h2>
              </div>

              <div className="row">
                <div className="col-sm-12 col-md-6 mb-3">
                  <label >إجمالي كمية الخليط (جرام)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={totalWeight}
                    onChange={(e) => setTotalWeight(e.target.value)}
                  />
                </div>
                <div className="col-sm-12 col-md-6 mb-3">
                  <label >نسبة الدهون المستهدفة (%)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={fatPercent}
                    onChange={(e) => setFatPercent(e.target.value)}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-sm-12 col-md-4 mb-3">
                  <label >نسبة السكر (%)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={sugarPercent}
                    onChange={(e) => setSugarPercent(e.target.value)}
                  />
                </div>
                <div className="col-sm-12 col-md-4 mb-3">
                  <label >نسبة MSNF (%)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={msnfPercent}
                    onChange={(e) => setMsnfPercent(e.target.value)}
                  />
                </div>
                <div className="col-sm-12 col-md-4 mb-3">
                  <label >المثبتات / المستحلبات (%)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={stabilizerPercent}
                    onChange={(e) => setStabilizerPercent(e.target.value)}
                  />
                </div>
              </div>

              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{ fontSize: "16px", padding: "10px 30px" }}
                >
                  مسح البيانات
                </button>
              </div>

              {error && (
                <div className="alert alert-warning" role="alert">
                  {error}
                </div>
              )}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>النتائج المطلوبة للوصفة:</strong>
                  </h3>
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>المكون</th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>الوزن (جرام)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>صافي الدهون</td>
                          <td style={{ textAlign: "center", fontWeight: "bold" }}>{result.fatWeight.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>المواد الصلبة اللادهنية (MSNF)</td>
                          <td style={{ textAlign: "center", fontWeight: "bold" }}>{result.msnfWeight.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>السكر</td>
                          <td style={{ textAlign: "center", fontWeight: "bold" }}>{result.sugarWeight.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>المثبتات</td>
                          <td style={{ textAlign: "center", fontWeight: "bold" }}>{result.stabilizerWeight.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>السوائل (الماء/الحليب السائل)</td>
                          <td style={{ textAlign: "center", fontWeight: "bold", color: "#1a5f3f" }}>{result.waterWeight.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">تحليل القوام</div>
                      <div className="info-section-body">
                        <ul style={{ listStyle: "none", padding: 0, lineHeight: "2" }}>
                          <li>🍦 <strong>إجمالي المواد الصلبة:</strong> {result.totalSolids.toFixed(2)}%</li>
                          <li>💧 <strong>نسبة الماء:</strong> {(100 - result.totalSolids).toFixed(2)}%</li>
                        </ul>
                        <div className="alert alert-info mt-2" style={{ fontSize: "14px" }}>
                          المعدل المثالي للمواد الصلبة الكلية يتراوح بين 36% و 42% للحصول على أفضل قوام.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">كيفية استخدام حاسبة الايس كريم</div>
              <div className="info-section-body">
                <p>لصناعة ايس كريم متوازن، يجب تحديد النسب المئوية للمكونات الأساسية:</p>
                <ul>
                  <li><strong>الدهون:</strong> تمنح الايس كريم الدسامة والنكهة (النسبة المثالية 10-16%).</li>
                  <li><strong>MSNF:</strong> هي بروتينات الحليب ولاكتوز وسكر الحليب، تساعد في تماسك القوام (النسبة المثالية 9-11%).</li>
                  <li><strong>السكر:</strong> يخفض نقطة التجمد ويضيف الحلاوة (النسبة المثالية 14-18%).</li>
                  <li><strong>المثبتات:</strong> تمنع تكون بلورات الثلج الكبيرة (النسبة المثالية 0.3-0.5%).</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">أهمية موازنة المكونات</div>
              <div className="info-section-body">
                <p>إذا كانت نسبة الماء مرتفعة جداً، سيصبح الايس كريم ثلجياً وصلباً. وإذا كانت نسبة الدهون مرتفعة جداً، قد يترك طعماً دهنيًا غير مرغوب فيه على اللسان. استخدام الحاسبة يضمن لك:</p>
                <ol>
                  <li>تجنب القوام الرملي (بسبب زيادة اللاكتوز).</li>
                  <li>الحصول على سرعة ذوبان مناسبة.</li>
                  <li>قوام كريمي ناعم يسهل غرفه بالملعقة.</li>
                </ol>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">نصائح احترافية للبوظة المنزلية</div>
              <div className="info-section-body">
                <p>للحصول على أفضل النتائج عند استخدام الحاسبة:</p>
                <ul>
                  <li>استخدم ميزانًا رقميًا دقيقًا (بالميلغرام للمثبتات).</li>
                  <li>احرص على تبريد الخليط لمدة 4-12 ساعة قبل وضعه في ماكينة الايس كريم.</li>
                  <li>تأكد من أن مجموع المواد الصلبة لا يتجاوز 42% لتجنب القوام الثقيل جداً.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default IceCreamCalculator;